import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import QRCode from 'qrcode';
import { useAuth } from '../contexts/AuthContext';
import { authService, type TotpSignInChallenge } from '../services/authService';
import type { TotpEnrollmentDraft } from '../services/accountSecurity';
import { useAccountPasswordValidation } from '../hooks/useAccountPasswordValidation';
import { userService } from '../services/userService';
import {
  canAccessServiceDashboard,
  getUserRoleIdentity,
  getUserStatus,
  isActiveAdmin,
} from '../utils/accessControl';
import {
  getLastPushInitDiagnostics,
  getPushPermissionStatus,
  isPushLocalCleanupError,
  type PushInitResult,
} from '../services/pushNotificationService';
import { usePushActivation } from '../hooks/usePushActivation';
import { usePushPreferences } from '../hooks/usePushPreferences';
import type { PushPreferenceCategory } from '../services/pushPreferencesService';
import { Alert, Badge, Button, Card, Input } from '../components/common/UIComponents';
import PasswordInput from '../components/common/PasswordInput';
import PasswordPolicyChecklist from '../components/common/PasswordPolicyChecklist';
import TotpNudgeCard from '../components/common/TotpNudgeCard';
import { PageErrorState, PageLoadingState } from '../components/common/ViewStates';
import { ServiceWorkspaceShell } from '../components/Service/ServiceWorkspaceShell';
import ServiceWorkspaceBackLink from '../components/Service/ServiceWorkspaceBackLink';
import {
  BellAlertIcon,
  LockClosedIcon,
  ExclamationCircleIcon,
  ExclamationTriangleIcon,
  PencilIcon,
  ShieldCheckIcon,
  TagIcon,
  TrashIcon,
  UserCircleIcon,
} from '@heroicons/react/24/outline';

const DELETE_ACCOUNT_CONFIRMATION_PHRASE = 'USUŃ';
const NORMALIZED_DELETE_ACCOUNT_PHRASE = 'USUN';

const PUSH_CATEGORY_PRESENTATION: Array<{
  key: PushPreferenceCategory;
  label: string;
  description?: string;
}> = [
  { key: 'repairWorkflow', label: 'Zlecenia i przebieg naprawy' },
  { key: 'chat', label: 'Wiadomości' },
  { key: 'reviewInvitations', label: 'Prośby o opinię' },
  { key: 'community', label: 'Społeczność' },
  { key: 'suggestions', label: 'Sugestie' },
  { key: 'specializations', label: 'Specjalizacje i katalog sprzętu' },
  { key: 'productFeedback', label: 'Zgłoszenia do zespołu produktu' },
  {
    key: 'serviceAndAdminOperations',
    label: 'Konto serwisu i zadania administracyjne',
    description: 'Serwis i administracja — wspólna kategoria dla operacji tych obszarów.',
  },
];

const SettingsPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { currentUser, userProfile, claims, loading: profileLoading } = useAuth();
  const [isEditingBasic, setIsEditingBasic] = useState(false);
  const [basicLoading, setBasicLoading] = useState(false);
  const [basicError, setBasicError] = useState<string | null>(null);
  const [basicSuccess, setBasicSuccess] = useState<string | null>(null);
  const [isChangingPassword, setIsChangingPassword] = useState(false);
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [passwordError, setPasswordError] = useState<string | null>(null);
  const [passwordSuccess, setPasswordSuccess] = useState<string | null>(null);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [mfaMode, setMfaMode] = useState<'idle' | 'enroll-reauth' | 'enroll-code' | 'disable'>(
    'idle'
  );
  const [mfaPassword, setMfaPassword] = useState('');
  const [mfaCode, setMfaCode] = useState('');
  const [mfaDraft, setMfaDraft] = useState<TotpEnrollmentDraft | null>(null);
  const [mfaDisableChallenge, setMfaDisableChallenge] = useState<TotpSignInChallenge | null>(null);
  const [mfaQrDataUrl, setMfaQrDataUrl] = useState<string | null>(null);
  const [mfaLoading, setMfaLoading] = useState(false);
  const [mfaError, setMfaError] = useState<string | null>(null);
  const [mfaSuccess, setMfaSuccess] = useState<string | null>(null);
  const [, setMfaRefreshTick] = useState(0);
  const [pushLoading, setPushLoading] = useState(false);
  const [pushMessage, setPushMessage] = useState<string | null>(null);
  const [pushError, setPushError] = useState<string | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [deleteSuccess, setDeleteSuccess] = useState<string | null>(null);
  const [deleteBlockedAreas, setDeleteBlockedAreas] = useState<
    Array<{ area: string; count: number | null; reason: string }>
  >([]);
  const [isDangerZoneExpanded, setIsDangerZoneExpanded] = useState(false);
  const [reapplyLoading, setReapplyLoading] = useState(false);
  const [reapplyMessage, setReapplyMessage] = useState<string | null>(null);
  const [reapplyError, setReapplyError] = useState<string | null>(null);
  const [pushPermission, setPushPermission] = useState<NotificationPermission | 'unsupported'>(
    getPushPermissionStatus()
  );
  const { snapshot: pushSnapshot, enable: enablePush, disable: disablePush } = usePushActivation();
  const pushPreferences = usePushPreferences();

  const settingsQuery = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const specializationSuggestionId = (settingsQuery.get('specializationSuggestionId') || '').trim();
  const requestedSettingsSection = (settingsQuery.get('section') || '').trim();
  const requestedSettingsAction = (settingsQuery.get('action') || '').trim();
  const fromSpecializationDecision =
    settingsQuery.get('notification') === 'specialization_decision' && !!specializationSuggestionId;

  const roleIdentity = getUserRoleIdentity(userProfile);
  const normalizedStatus = getUserStatus(userProfile, claims) || userProfile?.status || null;
  const isService = roleIdentity === 'serviceAdmin';
  const isApprovedService = canAccessServiceDashboard(userProfile, claims);
  const isEffectiveAdmin = isActiveAdmin(userProfile, claims);
  const canSeeServiceAndAdminPreferences = isApprovedService || isEffectiveAdmin;
  const isServiceWorkspaceContext = isService && settingsQuery.get('workspace') === 'service';

  const userAgent = typeof navigator !== 'undefined' ? navigator.userAgent : '';
  const isSafariBrowser =
    /Safari/i.test(userAgent) && !/Chrome|CriOS|Edg|OPR|Firefox|FxiOS/i.test(userAgent);
  const isIOSDevice = /iPhone|iPad|iPod/i.test(userAgent);
  const showSafariPushHint = pushPermission === 'unsupported' && (isSafariBrowser || isIOSDevice);
  const requiresPasswordConfirmation =
    currentUser?.providerData.some((provider) => provider.providerId === 'password') ?? false;
  const canReauthenticateWithGoogle =
    currentUser?.providerData.some((provider) => provider.providerId === 'google.com') ?? false;
  const showDisablePushAction = pushPermission === 'granted' && pushSnapshot.status === 'active';
  const showEnablePushAction =
    !showDisablePushAction &&
    pushPermission !== 'denied' &&
    pushPermission !== 'unsupported' &&
    pushSnapshot.status !== 'unsupported';
  const pushStateResolving =
    pushSnapshot.uid !== currentUser?.uid || pushSnapshot.status === 'resolving';
  const mfaReadiness = authService.getTotpMfaReadiness(currentUser);
  const passwordValidation = useAccountPasswordValidation(newPassword);
  const isPasswordMismatch = confirmPassword.length > 0 && newPassword !== confirmPassword;
  const canSubmitPassword =
    Boolean(newPassword) &&
    passwordValidation.isValid &&
    !isPasswordMismatch &&
    newPassword === confirmPassword;

  useEffect(() => {
    setPushPermission(getPushPermissionStatus());
  }, [currentUser?.uid, pushSnapshot.status]);

  useEffect(() => {
    if (requestedSettingsSection !== 'security' || requestedSettingsAction !== 'enable-2fa') {
      return;
    }
    if (mfaReadiness.status !== 'not-enrolled') {
      return;
    }
    setMfaMode((current) => (current === 'idle' ? 'enroll-reauth' : current));
    setMfaError(null);
    setMfaSuccess(null);
  }, [requestedSettingsSection, requestedSettingsAction, mfaReadiness.status]);

  const handleRefresh = () => {
    window.location.reload();
  };

  const handleBasicSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!currentUser) return;

    const formData = new FormData(event.currentTarget);
    const newDisplayName = (formData.get('displayName') as string) || '';

    if (!newDisplayName.trim()) {
      setBasicError('Nazwa wyświetlana nie może być pusta.');
      return;
    }

    setBasicLoading(true);
    setBasicError(null);
    setBasicSuccess(null);

    try {
      await authService.updateUserDisplayName(currentUser, newDisplayName.trim());
      setBasicSuccess('Zaktualizowano dane konta.');
      setIsEditingBasic(false);
      handleRefresh();
    } catch (error: any) {
      setBasicError(authService.mapFirebaseError(error?.code) || 'Nie udało się zapisać zmian.');
    } finally {
      setBasicLoading(false);
    }
  };

  const handleReapplyVerification = async () => {
    setReapplyLoading(true);
    setReapplyMessage(null);
    setReapplyError(null);
    try {
      await userService.requestServiceReverification();
      setReapplyMessage(
        'Wysłano profil serwisu do ponownej weryfikacji. Oczekuj na decyzję administratora.'
      );
      handleRefresh();
    } catch (error) {
      console.error('[Verification] Błąd ponownej weryfikacji:', error);
      setReapplyError('Nie udało się wysłać prośby o ponowną weryfikację.');
    } finally {
      setReapplyLoading(false);
    }
  };

  const normalizeDeletePhrase = (phrase: string): string => {
    return String(phrase || '')
      .trim()
      .toUpperCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '');
  };

  const mapPushResultToMessage = (result: PushInitResult): string => {
    switch (result) {
      case 'enabled':
        return 'Powiadomienia push zostały włączone na tym urządzeniu.';
      case 'already-enabled':
        return 'Powiadomienia push są już aktywne.';
      case 'permission-default':
        return 'Nie nadano zgody na powiadomienia.';
      case 'permission-denied':
        return 'Przeglądarka blokuje powiadomienia. Zmień to w ustawieniach strony.';
      case 'missing-vapid':
        return 'Brakuje konfiguracji VAPID_KEY w aplikacji.';
      case 'brave-push-service-disabled':
        return 'Brave blokuje usługę push. Włącz w Brave: Ustawienia -> Prywatność i bezpieczeństwo -> "Use Google services for push messaging", zrestartuj Brave i spróbuj ponownie.';
      case 'service-worker-error':
        return 'Błąd rejestracji service workera push. Odśwież stronę i spróbuj ponownie.';
      case 'subscription-error':
        return 'Błąd odczytu subskrypcji push w przeglądarce.';
      case 'token-error':
        return 'Nie udało się utworzyć tokenu push (subskrypcja przeglądarki).';
      case 'register-error':
        return 'Token push został utworzony, ale nie udało się zapisać go po stronie aplikacji.';
      case 'activation-unconfirmed':
        return 'Zapis aktywacji nie został potwierdzony po stronie aplikacji. Powiadomienia pozostają nieaktywne; spróbuj ponownie.';
      case 'no-token':
        return 'Nie udało się pobrać tokenu push. Sprawdź uprawnienia strony i ustawienia przeglądarki, potem spróbuj ponownie.';
      default:
        return 'Twoja przeglądarka nie wspiera powiadomień push.';
    }
  };

  const getPushStatusText = () => {
    if (pushPermission === 'unsupported') {
      return 'Ta przeglądarka nie wspiera Web Push.';
    }
    if (pushPermission === 'granted') {
      if (pushStateResolving) {
        return 'Sprawdzanie aktywacji push na tym urządzeniu...';
      }
      if (pushSnapshot.status === 'active') {
        return 'Powiadomienia push są aktywne na tym urządzeniu.';
      }
      if (pushSnapshot.status === 'error') {
        return 'Nie udało się potwierdzić aktywacji push na tym urządzeniu. Spróbuj ponownie.';
      }
      return 'Zgoda jest nadana, ale push nie został jeszcze aktywowany.';
    }
    if (pushPermission === 'denied') {
      return 'Powiadomienia są zablokowane w ustawieniach przeglądarki.';
    }
    return 'Powiadomienia nie są jeszcze włączone.';
  };

  const getMfaStatusText = () => {
    switch (mfaReadiness.status) {
      case 'active':
        return 'Konto wymaga kodu z aplikacji uwierzytelniającej przy logowaniu.';
      case 'not-enrolled':
        return 'Po włączeniu przy logowaniu poprosimy też o kod z aplikacji uwierzytelniającej.';
      case 'unavailable':
        return 'Weryfikacja dwuetapowa jest chwilowo niedostępna. Spróbuj później.';
      default:
        return 'Weryfikacja dwuetapowa jest chwilowo niedostępna. Spróbuj później.';
    }
  };

  const resetMfaPanel = () => {
    setMfaMode('idle');
    setMfaPassword('');
    setMfaCode('');
    setMfaDraft(null);
    setMfaDisableChallenge(null);
    setMfaQrDataUrl(null);
    setMfaError(null);
  };

  const reauthenticateForSecurityChange = async () => {
    if (requiresPasswordConfirmation) {
      await authService.reauthenticateWithPassword(mfaPassword);
      return;
    }
    if (canReauthenticateWithGoogle) {
      await authService.reauthenticateWithGoogle();
      return;
    }

    const error = new Error(
      'To działanie wymaga potwierdzenia logowania emailem lub Google.'
    ) as Error & { code: string };
    error.code = 'auth/provider-mismatch';
    throw error;
  };

  const handleStartMfaEnrollment = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setMfaLoading(true);
    setMfaError(null);
    setMfaSuccess(null);

    try {
      await reauthenticateForSecurityChange();
      const draft = await authService.startTotpEnrollment();
      const qrDataUrl = await QRCode.toDataURL(draft.qrCodeUrl, {
        margin: 1,
        width: 220,
        color: {
          dark: '#10211f',
          light: '#ffffff',
        },
      });
      setMfaDraft(draft);
      setMfaQrDataUrl(qrDataUrl);
      setMfaPassword('');
      setMfaMode('enroll-code');
    } catch (error: any) {
      setMfaError(authService.mapFirebaseError(error?.code) || 'Nie udało się rozpocząć 2FA.');
    } finally {
      setMfaLoading(false);
    }
  };

  const handleFinalizeMfaEnrollment = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!mfaDraft) return;

    setMfaLoading(true);
    setMfaError(null);
    setMfaSuccess(null);

    try {
      await authService.finalizeTotpEnrollment(mfaDraft.secret, mfaCode);
      setMfaSuccess('Weryfikacja dwuetapowa została włączona.');
      resetMfaPanel();
      setMfaRefreshTick((current) => current + 1);
    } catch (error: any) {
      setMfaError(authService.mapFirebaseError(error?.code) || 'Nie udało się potwierdzić kodu.');
    } finally {
      setMfaLoading(false);
    }
  };

  const handleDisableMfa = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setMfaLoading(true);
    setMfaError(null);
    setMfaSuccess(null);

    try {
      if (mfaDisableChallenge) {
        await authService.completeTotpReauthentication(mfaDisableChallenge, mfaCode);
      } else {
        await reauthenticateForSecurityChange();
        if (mfaReadiness.hasTotpEnrollment) {
          setMfaError('Nie udało się potwierdzić drugiego składnika. Spróbuj ponownie.');
          return;
        }
      }
      await authService.disableTotpEnrollment();
      setMfaSuccess('Weryfikacja dwuetapowa została wyłączona.');
      resetMfaPanel();
      setMfaRefreshTick((current) => current + 1);
    } catch (error: any) {
      if (authService.isMfaRequiredError(error)) {
        setMfaDisableChallenge(error.mfaChallenge);
        setMfaCode('');
        setMfaError(null);
        return;
      }
      setMfaError(
        authService.mapFirebaseError(error?.code) ||
          'Nie udało się potwierdzić drugiego składnika. Spróbuj ponownie.'
      );
    } finally {
      setMfaLoading(false);
    }
  };

  const handlePasswordSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    setPasswordLoading(true);
    setPasswordError(null);
    setPasswordSuccess(null);

    if (!passwordValidation.isValid) {
      setPasswordError('Hasło nie spełnia wymagań bezpieczeństwa.');
      setPasswordLoading(false);
      return;
    }
    if (newPassword !== confirmPassword) {
      setPasswordError('Hasła nie są zgodne.');
      setPasswordLoading(false);
      return;
    }

    try {
      await authService.updateUserPassword(newPassword);
      setPasswordSuccess('Hasło zostało pomyślnie zmienione.');
      setIsChangingPassword(false);
      setNewPassword('');
      setConfirmPassword('');
    } catch (error: any) {
      setPasswordError(authService.mapFirebaseError(error?.code) || 'Nie udało się zmienić hasła.');
    } finally {
      setPasswordLoading(false);
    }
  };

  const handleEnablePush = async () => {
    if (!currentUser?.uid) return;
    setPushLoading(true);
    setPushError(null);
    setPushMessage(null);
    try {
      const result = await enablePush();
      setPushPermission(getPushPermissionStatus());
      if (result === 'enabled' || result === 'already-enabled') {
        setPushMessage(mapPushResultToMessage(result));
      } else {
        setPushError(mapPushResultToMessage(result));
        const diagnostics = getLastPushInitDiagnostics();
        if (diagnostics) {
          console.warn('[Push][Settings] activation failed', diagnostics);
        }
      }
    } catch (error) {
      console.error('[Push] Błąd włączania push:', error);
      const diagnostics = getLastPushInitDiagnostics();
      if (diagnostics) {
        console.warn('[Push][Settings] activation exception', diagnostics);
      }
      setPushError('Nie udało się włączyć powiadomień push.');
    } finally {
      setPushLoading(false);
    }
  };

  const handleDisablePush = async () => {
    if (!currentUser?.uid) return;
    setPushLoading(true);
    setPushError(null);
    setPushMessage(null);
    try {
      const disabled = await disablePush();
      if (disabled) {
        setPushMessage(
          'Powiadomienia push zostały wyłączone na tym urządzeniu. Zgoda przeglądarki pozostaje aktywna.'
        );
      } else {
        setPushError('Brak aktywnego tokenu push do wyłączenia.');
      }
    } catch (error) {
      console.error('[Push] Błąd wyłączania push:', error);
      if (isPushLocalCleanupError(error)) {
        setPushError(
          'Push został wyłączony po stronie aplikacji, ale przeglądarka nie potwierdziła pełnego lokalnego czyszczenia. Odśwież stronę przed ponowną próbą włączenia.'
        );
      } else {
        setPushError('Nie udało się wyłączyć powiadomień push.');
      }
    } finally {
      setPushPermission(getPushPermissionStatus());
      setPushLoading(false);
    }
  };

  const handlePushCategoryToggle = async (category: PushPreferenceCategory, enabled: boolean) => {
    try {
      await pushPreferences.updateCategory(category, enabled);
    } catch (error) {
      console.warn('[Push][Settings] preference update failed', { category, error });
    }
  };

  const handleDeleteAccount = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!currentUser) return;

    const formData = new FormData(event.currentTarget);
    const confirmationPhrase = String(formData.get('deleteConfirmationPhrase') || '');
    const currentPassword = String(formData.get('deleteCurrentPassword') || '');

    setDeleteError(null);
    setDeleteSuccess(null);
    setDeleteBlockedAreas([]);

    const normalizedConfirmation = normalizeDeletePhrase(confirmationPhrase);
    if (normalizedConfirmation !== NORMALIZED_DELETE_ACCOUNT_PHRASE) {
      setDeleteError(
        `Aby kontynuować, wpisz dokładnie frazę „${DELETE_ACCOUNT_CONFIRMATION_PHRASE}”.`
      );
      return;
    }

    setDeleteLoading(true);
    try {
      if (requiresPasswordConfirmation) {
        await authService.reauthenticateWithPassword(currentPassword);
      }

      const result = await userService.deleteOwnAccount(confirmationPhrase);

      // Hard guarantee: ensure Firebase Auth account is actually removed.
      // In case backend cleanup succeeded but auth user still exists (e.g. stale deploy),
      // try a direct client-side delete after recent reauthentication.
      try {
        await currentUser.reload();
        if (currentUser.email) {
          await currentUser.delete();
        }
      } catch (postDeleteCheckError: any) {
        const code = String(postDeleteCheckError?.code || '');
        // `auth/user-not-found` means account is already deleted in Auth - this is expected.
        if (!code.includes('auth/user-not-found')) {
          throw postDeleteCheckError;
        }
      }

      setDeleteBlockedAreas(result.blocked || []);
      setDeleteSuccess(
        'Konto zostało usunięte. Wszystkie aktywne sesje wygasły, a dostęp do konta został odebrany.'
      );

      try {
        await authService.logout();
      } catch (_logoutError) {
        // Ignore explicit sign-out errors after backend account deletion.
      }

      navigate('/login', { replace: true });
    } catch (error: any) {
      const code = String(error?.code || '');
      if (code.includes('auth/wrong-password') || code.includes('auth/invalid-credential')) {
        setDeleteError('Podane hasło jest nieprawidłowe.');
      } else if (code.includes('auth/too-many-requests')) {
        setDeleteError('Zbyt wiele nieudanych prób. Odczekaj chwilę i spróbuj ponownie.');
      } else if (
        code.includes('auth/requires-recent-login') ||
        code.includes('failed-precondition')
      ) {
        setDeleteError('Dla bezpieczeństwa zaloguj się ponownie i ponów usunięcie konta.');
      } else {
        setDeleteError(userService.mapDeleteOwnAccountError(error));
      }
    } finally {
      setDeleteLoading(false);
    }
  };

  if (profileLoading) {
    return (
      <div className="py-8 md:py-12 max-w-2xl mx-auto">
        <PageLoadingState
          title="Ładowanie ustawień konta"
          message="Pobieramy dane konta i preferencje bezpieczeństwa."
        />
      </div>
    );
  }

  if (!currentUser) {
    return (
      <div className="py-8 md:py-12 max-w-xl mx-auto">
        <PageErrorState
          title="Brak dostępu do ustawień"
          message="Aby zarządzać ustawieniami konta, zaloguj się ponownie."
          actionLabel="Wróć do logowania"
          onAction={() => navigate('/login')}
        />
      </div>
    );
  }

  const settingsCardClass =
    'rs-form-section !rounded-none !border-x-0 !border-b-0 !bg-transparent !px-1 !py-4 sm:!px-0 sm:!py-5';
  const settingsDeleteCardClass = isServiceWorkspaceContext
    ? 'border-status-error-500/40 bg-status-error-500/5 p-3 sm:p-4'
    : 'border-status-error-500/40 bg-status-error-500/5 p-3.5 sm:p-5';
  const pageContent = (
    <div
      className={`w-full max-w-5xl mx-auto ${
        isServiceWorkspaceContext ? 'space-y-3 sm:space-y-4' : 'space-y-3.5 sm:space-y-5'
      }`}
    >
      {isServiceWorkspaceContext && (
        <ServiceWorkspaceBackLink onClick={() => navigate('/app/service')} />
      )}

      {fromSpecializationDecision && (
        <Alert variant="info" className="px-4 py-3 text-sm">
          Powiadomienie dotyczy decyzji o Twojej sugestii specjalizacji (ID:{' '}
          {specializationSuggestionId}).
          {(isService || isApprovedService) && (
            <Button
              variant="neutral"
              size="sm"
              className="ml-2"
              onClick={() =>
                navigate(
                  `/app/service-profile?specializationSuggestionId=${specializationSuggestionId}`
                )
              }
            >
              Przejdź do danych serwisu
            </Button>
          )}
        </Alert>
      )}

      <Card className={settingsCardClass}>
        <h2 className="mb-3 flex items-center gap-2 text-base font-semibold text-primary-light-100 sm:text-lg">
          <UserCircleIcon className="w-5 h-5 text-ratuj-300" />
          Dane konta
        </h2>
        <div className="mb-3 flex flex-wrap items-center gap-1.5">
          {roleIdentity === 'serviceAdmin' ? (
            <Badge variant="accent" size="sm">
              Serwisant
            </Badge>
          ) : roleIdentity === 'admin' ? (
            <Badge variant="warning" size="sm">
              Administrator
            </Badge>
          ) : roleIdentity === 'client' ? (
            <Badge variant="primary" size="sm">
              Klient
            </Badge>
          ) : (
            <Badge variant="neutral" size="sm">
              Konto bez przypisanej roli
            </Badge>
          )}
          {normalizedStatus && (
            <Badge variant="neutral" size="sm">
              Status: {normalizedStatus}
            </Badge>
          )}
        </div>

        {basicError && (
          <Alert variant="error" className="mb-4">
            {basicError}
          </Alert>
        )}
        {basicSuccess && (
          <Alert variant="success" className="mb-4">
            {basicSuccess}
          </Alert>
        )}

        {isEditingBasic ? (
          <form onSubmit={handleBasicSubmit} className="space-y-4">
            <Input
              id="displayName"
              name="displayName"
              label="Nazwa wyświetlana"
              defaultValue={userProfile?.displayName || currentUser.displayName || ''}
              required
              disabled={basicLoading}
            />
            <div className="flex gap-3">
              <Button type="submit" variant="primary" size="sm" isLoading={basicLoading}>
                Zapisz
              </Button>
              <Button
                type="button"
                variant="neutral"
                size="sm"
                onClick={() => setIsEditingBasic(false)}
                disabled={basicLoading}
              >
                Anuluj
              </Button>
            </div>
          </form>
        ) : (
          <div className="space-y-3">
            <p className="text-sm text-primary-light-300">
              Zalogowany email:{' '}
              <span className="text-primary-light-100">
                {currentUser.email || 'Brak adresu email'}
              </span>
            </p>
            <p className="text-sm text-primary-light-300 flex items-center gap-1.5">
              <TagIcon className="w-4 h-4 text-primary-light-400" />
              Nazwa konta:{' '}
              <span className="text-primary-light-100">
                {userProfile?.displayName || currentUser.displayName || 'Brak nazwy'}
              </span>
            </p>
            <Button
              variant="secondary"
              size="sm"
              icon={<PencilIcon className="h-4 w-4" />}
              onClick={() => setIsEditingBasic(true)}
            >
              Edytuj dane konta
            </Button>
          </div>
        )}

        {isService && normalizedStatus !== 'active' && (
          <div className="mt-4 space-y-3 border-t border-primary-dark-300/60 pt-3">
            {normalizedStatus === 'pending' && (
              <Alert variant="warning">
                Profil serwisu oczekuje na zatwierdzenie administratora.
              </Alert>
            )}
            {normalizedStatus === 'rejected' && (
              <>
                <Alert variant="error">
                  Profil serwisu został odrzucony. Możesz wysłać go ponownie do weryfikacji.
                </Alert>
                {reapplyError && <Alert variant="error">{reapplyError}</Alert>}
                {reapplyMessage && <Alert variant="success">{reapplyMessage}</Alert>}
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={handleReapplyVerification}
                  isLoading={reapplyLoading}
                >
                  Poproś o ponowną weryfikację
                </Button>
              </>
            )}
          </div>
        )}
      </Card>

      <Card className={settingsCardClass} id="notifications">
        <h2 className="mb-3 flex items-center gap-2 text-base font-semibold text-primary-light-100 sm:text-lg">
          <BellAlertIcon className="w-5 h-5 text-ratuj-300" />
          Powiadomienia
        </h2>
        <section aria-labelledby="settings-push-installation-heading" className="space-y-3">
          <div>
            <h3
              id="settings-push-installation-heading"
              className="text-sm font-semibold text-primary-light-100 sm:text-base"
            >
              Powiadomienia na tym urządzeniu
            </h3>
            <p className="mt-1 text-sm text-primary-light-300">
              Czy ta przeglądarka lub urządzenie może odbierać powiadomienia push?
            </p>
            <p className="mt-2 text-sm text-primary-light-300">{getPushStatusText()}</p>
          </div>
          {pushError && <Alert variant="error">{pushError}</Alert>}
          {pushMessage && <Alert variant="success">{pushMessage}</Alert>}
          <div className="flex flex-wrap gap-3">
            {showDisablePushAction ? (
              <Button
                variant="neutral"
                size="sm"
                onClick={handleDisablePush}
                disabled={pushLoading || pushStateResolving}
              >
                Wyłącz powiadomienia
              </Button>
            ) : showEnablePushAction ? (
              <Button
                variant="secondary"
                size="sm"
                onClick={handleEnablePush}
                disabled={pushLoading || pushStateResolving}
                isLoading={pushLoading}
              >
                Włącz powiadomienia
              </Button>
            ) : null}
          </div>
          {pushPermission === 'denied' && (
            <Alert variant="warning" icon={<ExclamationCircleIcon className="w-5 h-5" />}>
              Aby odblokować push, włącz powiadomienia dla tej strony w ustawieniach przeglądarki.
            </Alert>
          )}
          {showSafariPushHint && (
            <Alert variant="info">
              Safari może nie wspierać Web Push w tej konfiguracji. Na iPhonie/iPadzie: 1) otwórz
              stronę w Safari, 2) wybierz „Udostępnij” i „Dodaj do ekranu głównego”, 3) uruchom
              aplikację z ikony i dopiero wtedy włącz powiadomienia.
            </Alert>
          )}
        </section>

        <section
          aria-labelledby="settings-push-categories-heading"
          className="mt-6 border-t border-primary-dark-300/60 pt-5"
        >
          <div>
            <h3
              id="settings-push-categories-heading"
              className="text-sm font-semibold text-primary-light-100 sm:text-base"
            >
              Jakie powiadomienia push chcesz dostawać
            </h3>
            <p className="mt-1 max-w-2xl text-sm text-primary-light-300">
              Te preferencje dotyczą Twojego konta i wszystkich jego włączonych urządzeń. Wyłączenie
              tego urządzenia nie wyłącza żadnej kategorii.
            </p>
          </div>

          {pushPreferences.status === 'loading' && (
            <p className="mt-4 text-sm text-primary-light-400" role="status">
              Ładowanie preferencji push…
            </p>
          )}
          {pushPreferences.status === 'unavailable' && (
            <Alert variant="error" className="mt-4">
              Nie udało się odczytać preferencji push. Zmiany są chwilowo niedostępne — spróbuj
              ponownie później.
            </Alert>
          )}
          {pushPreferences.status === 'ready' && (
            <div className="mt-4 divide-y divide-primary-dark-300/50">
              {PUSH_CATEGORY_PRESENTATION.filter(
                (category) =>
                  category.key !== 'serviceAndAdminOperations' || canSeeServiceAndAdminPreferences
              ).map((category) => {
                const isPending = pushPreferences.pendingCategories.has(category.key);
                const categoryError =
                  pushPreferences.writeError?.category === category.key
                    ? pushPreferences.writeError.error
                    : null;
                const inputId = `push-preference-${category.key}`;
                return (
                  <div
                    key={category.key}
                    className="flex min-w-0 items-start justify-between gap-4 py-3.5 first:pt-0 last:pb-0"
                    data-testid={`push-preference-row-${category.key}`}
                    aria-busy={isPending}
                  >
                    <div className="min-w-0 flex-1 pr-2">
                      <>
                        <label
                          htmlFor={inputId}
                          className="block cursor-pointer text-sm font-medium leading-snug text-primary-light-100 focus-within:outline-none"
                        >
                          {category.label}
                        </label>
                        {category.description && (
                          <p className="mt-1 text-xs leading-relaxed text-primary-light-400">
                            {category.description}
                          </p>
                        )}
                        {isPending && (
                          <p className="mt-1 text-xs text-primary-light-400" role="status">
                            Zapisywanie…
                          </p>
                        )}
                        {categoryError && (
                          <p className="mt-1 text-xs text-status-error-300" role="alert">
                            Nie zapisano. Spróbuj ponownie.
                          </p>
                        )}
                      </>
                    </div>
                    <label
                      htmlFor={inputId}
                      className="relative mt-0.5 flex min-h-[44px] min-w-[52px] shrink-0 cursor-pointer items-center justify-end"
                    >
                      <input
                        id={inputId}
                        type="checkbox"
                        role="switch"
                        className="peer sr-only"
                        checked={pushPreferences.values[category.key]}
                        disabled={isPending}
                        onChange={(event) =>
                          void handlePushCategoryToggle(category.key, event.target.checked)
                        }
                        aria-label={category.label}
                      />
                      <span
                        aria-hidden="true"
                        className="relative h-7 w-12 rounded-full border border-primary-light-400/40 bg-primary-dark-300/70 transition-colors peer-checked:border-ratuj-300 peer-checked:bg-ratuj-500/80 peer-focus-visible:ring-2 peer-focus-visible:ring-ratuj-300 peer-disabled:cursor-not-allowed peer-disabled:opacity-60"
                      />
                      <span
                        aria-hidden="true"
                        className="pointer-events-none absolute right-[22px] top-2 h-5 w-5 rounded-full bg-primary-light-100 shadow transition-transform peer-checked:translate-x-5"
                      />
                    </label>
                  </div>
                );
              })}
            </div>
          )}
        </section>
      </Card>

      <Card className={settingsCardClass} id="security">
        <h2 className="mb-3 flex items-center gap-2 text-base font-semibold text-primary-light-100 sm:text-lg">
          <LockClosedIcon className="w-5 h-5 text-ratuj-300" />
          Bezpieczeństwo
        </h2>

        <TotpNudgeCard placement="settings" className="mb-4" />

        <div className="mb-5 divide-y divide-primary-dark-300/60 border-y border-primary-dark-300/60">
          <div className="flex flex-col gap-3 py-4 sm:flex-row sm:items-start sm:justify-between">
            <div className="min-w-0">
              <div className="flex items-center gap-2">
                <ShieldCheckIcon className="h-5 w-5 flex-none text-ratuj-300" aria-hidden="true" />
                <h3 className="text-sm font-semibold text-primary-light-100">
                  Weryfikacja dwuetapowa
                </h3>
                <Badge variant={mfaReadiness.status === 'active' ? 'success' : 'neutral'} size="sm">
                  {mfaReadiness.status === 'active'
                    ? 'Włączona'
                    : mfaReadiness.status === 'not-enrolled'
                      ? 'Wyłączona'
                      : 'Niedostępna'}
                </Badge>
              </div>
              <p className="mt-2 text-sm leading-6 text-primary-light-300">{getMfaStatusText()}</p>
            </div>
            {mfaReadiness.status === 'active' ? (
              <Button
                type="button"
                variant="neutral"
                size="sm"
                onClick={() => {
                  setMfaMode('disable');
                  setMfaError(null);
                  setMfaSuccess(null);
                  setMfaDisableChallenge(null);
                }}
                className="w-full sm:w-auto"
              >
                Zarządzaj
              </Button>
            ) : mfaReadiness.status === 'not-enrolled' ? (
              <Button
                type="button"
                variant="secondary"
                size="sm"
                onClick={() => {
                  setMfaMode('enroll-reauth');
                  setMfaError(null);
                  setMfaSuccess(null);
                }}
                className="w-full sm:w-auto"
              >
                Włącz 2FA
              </Button>
            ) : (
              <Button
                type="button"
                variant="neutral"
                size="sm"
                disabled
                className="w-full sm:w-auto"
              >
                Niedostępne
              </Button>
            )}
          </div>

          {(mfaError || mfaSuccess || mfaMode !== 'idle') && (
            <div className="space-y-4 py-4">
              {mfaError && <Alert variant="error">{mfaError}</Alert>}
              {mfaSuccess && <Alert variant="success">{mfaSuccess}</Alert>}

              {mfaMode === 'enroll-reauth' && (
                <form onSubmit={handleStartMfaEnrollment} className="space-y-4">
                  {requiresPasswordConfirmation ? (
                    <PasswordInput
                      id="mfaCurrentPassword"
                      label="Aktualne hasło"
                      value={mfaPassword}
                      onChange={(event) => setMfaPassword(event.target.value)}
                      autoComplete="current-password"
                      required
                      disabled={mfaLoading}
                    />
                  ) : (
                    <Alert variant="info">
                      Potwierdź konto przez Google, aby włączyć weryfikację dwuetapową.
                    </Alert>
                  )}
                  <div className="flex flex-col gap-3 sm:flex-row">
                    <Button
                      type="submit"
                      variant="primary"
                      size="sm"
                      isLoading={mfaLoading}
                      disabled={requiresPasswordConfirmation && !mfaPassword.trim()}
                    >
                      Kontynuuj
                    </Button>
                    <Button
                      type="button"
                      variant="neutral"
                      size="sm"
                      onClick={resetMfaPanel}
                      disabled={mfaLoading}
                    >
                      Anuluj
                    </Button>
                  </div>
                </form>
              )}

              {mfaMode === 'enroll-code' && mfaDraft && (
                <form onSubmit={handleFinalizeMfaEnrollment} className="space-y-4">
                  <div className="grid gap-4 sm:grid-cols-[auto,1fr] sm:items-start">
                    {mfaQrDataUrl && (
                      <div className="w-fit rounded-lg bg-white p-3">
                        <img src={mfaQrDataUrl} alt="Kod QR 2FA" className="h-44 w-44" />
                      </div>
                    )}
                    <div className="min-w-0 space-y-3">
                      <p className="text-sm leading-6 text-primary-light-300">
                        Zeskanuj kod w aplikacji uwierzytelniającej albo wpisz klucz ręcznie.
                      </p>
                      <div>
                        <p className="text-xs font-semibold text-primary-light-400">Klucz ręczny</p>
                        <p className="mt-1 break-all rounded-lg border border-primary-dark-300/50 bg-primary-dark-100/50 p-3 font-mono text-sm text-primary-light-100">
                          {mfaDraft.secretKey}
                        </p>
                      </div>
                      <p className="text-xs leading-5 text-primary-light-400">
                        Kod odświeża się co {mfaDraft.codeIntervalSeconds} sekund.
                      </p>
                    </div>
                  </div>

                  <Input
                    id="mfaEnrollmentCode"
                    type="text"
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    label="Kod z aplikacji"
                    placeholder="123456"
                    value={mfaCode}
                    onChange={(event) => setMfaCode(event.target.value)}
                    disabled={mfaLoading}
                    required
                  />

                  <div className="flex flex-col gap-3 sm:flex-row">
                    <Button
                      type="submit"
                      variant="error"
                      size="sm"
                      isLoading={mfaLoading}
                      disabled={!mfaCode.trim()}
                    >
                      Włącz 2FA
                    </Button>
                    <Button
                      type="button"
                      variant="neutral"
                      size="sm"
                      onClick={resetMfaPanel}
                      disabled={mfaLoading}
                    >
                      Anuluj
                    </Button>
                  </div>
                </form>
              )}

              {mfaMode === 'disable' && (
                <form onSubmit={handleDisableMfa} className="space-y-4">
                  <Alert variant="info">
                    Aby wyłączyć weryfikację dwuetapową, potwierdź hasło i kod z aplikacji
                    uwierzytelniającej.
                  </Alert>
                  <Alert variant="error">
                    Potwierdź logowanie kodem z aplikacji uwierzytelniającej.
                  </Alert>
                  <Alert variant="warning">
                    Po wyłączeniu przy logowaniu nie będziemy prosić o kod z aplikacji
                    uwierzytelniającej.
                  </Alert>
                  {requiresPasswordConfirmation ? (
                    <PasswordInput
                      id="mfaDisableCurrentPassword"
                      label="Aktualne hasło"
                      value={mfaPassword}
                      onChange={(event) => setMfaPassword(event.target.value)}
                      autoComplete="current-password"
                      required
                      disabled={mfaLoading || Boolean(mfaDisableChallenge)}
                    />
                  ) : (
                    <Alert variant="info">
                      Potwierdź konto przez Google, aby wyłączyć weryfikację dwuetapową.
                    </Alert>
                  )}
                  {mfaDisableChallenge && (
                    <Input
                      id="mfaDisableCode"
                      type="text"
                      inputMode="numeric"
                      autoComplete="one-time-code"
                      label="Kod z aplikacji"
                      placeholder="123456"
                      value={mfaCode}
                      onChange={(event) => setMfaCode(event.target.value)}
                      disabled={mfaLoading}
                      required
                    />
                  )}
                  <div className="flex flex-col gap-3 sm:flex-row">
                    <Button
                      type="submit"
                      variant="primary"
                      size="sm"
                      isLoading={mfaLoading}
                      disabled={
                        mfaDisableChallenge
                          ? !mfaCode.trim()
                          : requiresPasswordConfirmation && !mfaPassword.trim()
                      }
                    >
                      Wyłącz 2FA
                    </Button>
                    <Button
                      type="button"
                      variant="neutral"
                      size="sm"
                      onClick={resetMfaPanel}
                      disabled={mfaLoading}
                    >
                      Anuluj
                    </Button>
                  </div>
                </form>
              )}
            </div>
          )}

          <div className="flex flex-col gap-3 py-4 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <h3 className="text-sm font-semibold text-primary-light-100">Hasło</h3>
              <p className="mt-1 text-sm leading-6 text-primary-light-300">
                Hasło dotyczy tylko logowania emailem. Logowanie przez Google pozostaje bez zmian.
              </p>
            </div>
            {!isChangingPassword && (
              <Button
                variant="neutral"
                size="sm"
                onClick={() => {
                  setIsChangingPassword(true);
                  setPasswordError(null);
                  setPasswordSuccess(null);
                }}
                className="w-full sm:w-auto"
              >
                Zmień hasło
              </Button>
            )}
          </div>
        </div>

        {passwordError && (
          <Alert variant="error" className="mb-3">
            {passwordError}
          </Alert>
        )}
        {passwordSuccess && (
          <Alert variant="success" className="mb-3">
            {passwordSuccess}
          </Alert>
        )}

        {isChangingPassword ? (
          <form onSubmit={handlePasswordSubmit} className="space-y-4">
            <PasswordInput
              id="newPassword"
              name="newPassword"
              label="Nowe hasło"
              placeholder="Minimum 8 znaków"
              value={newPassword}
              onChange={(event) => setNewPassword(event.target.value)}
              required
              minLength={8}
              autoComplete="new-password"
              disabled={passwordLoading}
            />
            <PasswordPolicyChecklist password={newPassword} validation={passwordValidation} />
            <PasswordInput
              id="confirmPassword"
              name="confirmPassword"
              label="Potwierdź hasło"
              placeholder="Powtórz nowe hasło"
              value={confirmPassword}
              onChange={(event) => setConfirmPassword(event.target.value)}
              required
              minLength={8}
              autoComplete="new-password"
              disabled={passwordLoading}
              error={isPasswordMismatch ? 'Hasła nie są zgodne.' : undefined}
            />
            <div className="flex flex-col gap-3 sm:flex-row">
              <Button
                type="submit"
                variant="primary"
                size="sm"
                isLoading={passwordLoading}
                disabled={!canSubmitPassword}
              >
                Zmień hasło
              </Button>
              <Button
                type="button"
                variant="neutral"
                size="sm"
                onClick={() => {
                  setIsChangingPassword(false);
                  setNewPassword('');
                  setConfirmPassword('');
                  setPasswordError(null);
                }}
                disabled={passwordLoading}
              >
                Anuluj
              </Button>
            </div>
          </form>
        ) : (
          <Button
            type="button"
            variant="neutral"
            size="sm"
            onClick={() => navigate('/bezpieczenstwo')}
          >
            Zobacz zasady bezpieczeństwa
          </Button>
        )}
      </Card>

      <Card className={settingsDeleteCardClass}>
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="min-w-0">
            <h2 className="flex items-center gap-2 text-base font-semibold text-status-error-500 sm:text-lg">
              <TrashIcon className="w-5 h-5" />
              Usuń konto
            </h2>
            <p className="mt-1 text-sm leading-relaxed text-primary-light-300">
              Nieodwracalna operacja. Opcje usunięcia są domyślnie ukryte.
            </p>
          </div>
          <Button
            type="button"
            variant="neutral"
            size="sm"
            onClick={() => setIsDangerZoneExpanded((current) => !current)}
            aria-expanded={isDangerZoneExpanded}
            data-testid="delete-account-toggle"
          >
            {isDangerZoneExpanded ? 'Ukryj opcje usunięcia' : 'Pokaż opcje usunięcia'}
          </Button>
        </div>

        {isDangerZoneExpanded && (
          <div
            className="mt-4 border-t border-status-error-500/22 pt-4"
            data-testid="danger-zone-expanded"
          >
            <Alert
              variant="warning"
              icon={<ExclamationTriangleIcon className="w-5 h-5" />}
              className="mb-4"
            >
              Ta operacja jest nieodwracalna. Po usunięciu konta utracisz dostęp do danych i
              wszystkich aktywnych sesji.
            </Alert>

            <p className="text-sm text-primary-light-300 mb-4">
              Usuwamy dane bezpośrednio należące do konta. Część danych współdzielonych (np.
              historia zleceń, wiadomości, opinie) może wymagać osobnej decyzji architektonicznej i
              zostanie jawnie oznaczona w raporcie usunięcia.
            </p>

            {deleteError && (
              <Alert variant="error" className="mb-3">
                {deleteError}
              </Alert>
            )}
            {deleteSuccess && (
              <Alert variant="success" className="mb-3">
                {deleteSuccess}
              </Alert>
            )}
            {deleteBlockedAreas.length > 0 && (
              <Alert variant="info" className="mb-3">
                <div className="space-y-1">
                  <p className="font-semibold">
                    Elementy wymagające dalszej decyzji architektonicznej:
                  </p>
                  <ul className="list-disc list-inside text-sm">
                    {deleteBlockedAreas.map((item) => (
                      <li key={`${item.area}-${item.reason}`}>
                        {item.area}
                        {item.count !== null ? ` (${item.count})` : ''} - {item.reason}
                      </li>
                    ))}
                  </ul>
                </div>
              </Alert>
            )}

            <form onSubmit={handleDeleteAccount} className="space-y-4">
              {requiresPasswordConfirmation && (
                <PasswordInput
                  id="deleteCurrentPassword"
                  name="deleteCurrentPassword"
                  label="Aktualne hasło"
                  placeholder="Wpisz aktualne hasło"
                  required
                  autoComplete="current-password"
                  disabled={deleteLoading}
                />
              )}

              <Input
                id="deleteConfirmationPhrase"
                name="deleteConfirmationPhrase"
                type="text"
                label={`Wpisz frazę potwierdzającą: ${DELETE_ACCOUNT_CONFIRMATION_PHRASE}`}
                placeholder={DELETE_ACCOUNT_CONFIRMATION_PHRASE}
                required
                disabled={deleteLoading}
                autoComplete="off"
                autoCapitalize="characters"
                autoCorrect="off"
                spellCheck={false}
                inputMode="text"
              />

              <div className="flex flex-col sm:flex-row gap-3">
                <Button
                  type="submit"
                  variant="error"
                  size="sm"
                  isLoading={deleteLoading}
                  disabled={deleteLoading}
                >
                  Usuń konto nieodwracalnie
                </Button>
                <Button
                  type="button"
                  variant="neutral"
                  size="sm"
                  onClick={() => setIsDangerZoneExpanded(false)}
                  disabled={deleteLoading}
                >
                  Anuluj
                </Button>
              </div>
            </form>
          </div>
        )}
      </Card>
    </div>
  );

  if (isServiceWorkspaceContext) {
    return (
      <ServiceWorkspaceShell containerClassName="max-w-5xl" contentClassName="space-y-1 p-0">
        {pageContent}
      </ServiceWorkspaceShell>
    );
  }

  return <div className="rs-page-gutter py-3 md:py-8">{pageContent}</div>;
};

export default SettingsPage;
